/**
 * <mb-featured-tabs> — the homepage "Featured this week" tab strip.
 *
 * Owns tab activation, roving-tabindex keyboard nav, the shared "View all"
 * link, and lazy card loading. Migrated out of the legacy initTabs() in
 * assets/modern-bold.js when the lazy loading went in.
 *
 * Lazy contract (markup from sections/modern-featured-tabs.liquid):
 *   Only the open tab ships cards. Every other panel holds an empty
 *   .mb-tabs__grid carrying:
 *     data-mb-tab-grid            — marks it as still to fetch
 *     data-mb-tab-url             — "/collections/<handle>?section_id=<id>"
 *     data-mb-tab-index           — this tab's index in the rendered section
 *   The fetch goes through the tab's own collection URL so the section can tell
 *   which tab to render; the response is the whole section, and this element
 *   lifts out the matching panel's grid. Tab buttons and the per-tab "View all"
 *   links are always server-rendered, so internal linking never depends on JS.
 *
 * A failed fetch leaves the panel empty and keeps its "View all" link, so the
 * tab still leads somewhere.
 */
class MBFeaturedTabs extends HTMLElement {
  #buttons = [];
  #panels = [];
  #viewAll = null;
  #fallbackUrl = '';
  #cleanups = [];

  connectedCallback() {
    this.#buttons = [...this.querySelectorAll('[role="tab"]')];
    this.#panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!this.#buttons.length) return;

    const section = this.closest('.shopify-section') ?? document;
    this.#viewAll = section.querySelector('[data-mb-tabs-view-all]');
    this.#fallbackUrl = this.#viewAll?.getAttribute('data-fallback-url') ?? '';

    this.#buttons.forEach((button, index) => {
      this.#on(button, 'click', () => this.activate(index));
      this.#on(button, 'keydown', (event) => this.#onKeydown(event, index));
    });

    // The open tab may itself be pending if a merchant reorders blocks.
    const open = this.#buttons.findIndex((b) => b.getAttribute('aria-selected') === 'true');
    this.#fill(this.#panels[open < 0 ? 0 : open]);
  }

  disconnectedCallback() {
    for (const off of this.#cleanups) off();
    this.#cleanups = [];
  }

  #on(target, event, handler) {
    target.addEventListener(event, handler);
    this.#cleanups.push(() => target.removeEventListener(event, handler));
  }

  activate(index) {
    this.#buttons.forEach((button, i) => {
      const on = i === index;
      button.setAttribute('aria-selected', on ? 'true' : 'false');
      button.tabIndex = on ? 0 : -1;
    });
    this.#panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
    this.#syncViewAll(index);
    this.#fill(this.#panels[index]);
  }

  #onKeydown(event, index) {
    const last = this.#buttons.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.#buttons[next].focus();
    this.activate(next);
  }

  #syncViewAll(index) {
    if (!this.#viewAll) return;
    const url = this.#buttons[index]?.getAttribute('data-collection-url') || this.#fallbackUrl;
    if (url) {
      this.#viewAll.setAttribute('href', url);
      this.#viewAll.removeAttribute('role');
      this.#viewAll.removeAttribute('aria-disabled');
    } else {
      this.#viewAll.removeAttribute('href');
      this.#viewAll.setAttribute('role', 'link');
      this.#viewAll.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * Placeholder cards for the length of the fetch. They reuse the real card's
   * media wrapper, which already carries the 4/5 ratio and the grey ground, so
   * the grid is the right height from the first frame and nothing moves when
   * the real cards land.
   */
  #showSkeleton(grid) {
    const count = Number(grid.dataset.mbTabCount) || 4;
    const card = `
      <div class="mb-colcard mb-colcard--skel" aria-hidden="true">
        <div class="mb-colcard__media-wrap"></div>
        <div class="mb-colcard__body">
          <span class="mb-skel-line mb-skel-line--title"></span>
          <span class="mb-skel-line mb-skel-line--badges"></span>
          <span class="mb-skel-line mb-skel-line--price"></span>
        </div>
      </div>`;
    grid.insertAdjacentHTML('beforeend', card.repeat(count));
  }

  async #fill(panel) {
    const grid = panel?.querySelector('[data-mb-tab-grid]');
    const url = grid?.dataset.mbTabUrl;
    if (!grid || !url) return;

    // Claim it before awaiting so a double click cannot fire two fetches.
    delete grid.dataset.mbTabUrl;
    grid.setAttribute('aria-busy', 'true');
    this.#showSkeleton(grid);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${response.status}`);
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const index = grid.dataset.mbTabIndex;
      const source = markup.querySelector(`[data-mb-tab-index="${index}"], [role="tabpanel"]:nth-of-type(${Number(index) + 1}) .mb-tabs__grid`);
      const cards = source?.children.length ? source : markup.querySelectorAll('[role="tabpanel"]')[index]?.querySelector('.mb-tabs__grid');
      if (cards?.children.length) grid.replaceChildren(...cards.children);
    } catch (error) {
      // Clear the skeleton: the panel keeps its "View all" link, so the tab
      // still leads somewhere rather than pulsing forever.
      grid.replaceChildren();
      console.warn('[mb-featured-tabs] could not load tab', error);
    } finally {
      grid.removeAttribute('aria-busy');
      grid.removeAttribute('data-mb-tab-grid');
    }
  }
}

customElements.define('mb-featured-tabs', MBFeaturedTabs);
